import React, { useState, useEffect } from "react";

// Custom components
import toastManager from "../../../../helpers/toastManager";
import { Heading1, Heading2 } from "../../../../components/Headings/Headings";
import {
  SquareIconNavButton,
  TextActionButton,
} from "../../../../components/Buttons/Buttons";
import { ColorPickerRow, SliderRow } from "./SettingsHelpers";

// API imports
import useAxios from "../../../../hooks/useAxios";
import jsonFormatAuth from "../../../../apis/jsonFormatAuth";

// Body segments
import { ReactComponent as Head } from "./BodySegments/head.svg";
import { ReactComponent as Torso } from "./BodySegments/torso.svg";
import { ReactComponent as LeftArm } from "./BodySegments/left_arm.svg";
import { ReactComponent as RightArm } from "./BodySegments/right_arm.svg";
import { ReactComponent as LeftLeg } from "./BodySegments/left_leg.svg";
import { ReactComponent as RightLeg } from "./BodySegments/right_leg.svg";

// Icons
import { ReactComponent as SkinExtractionIcon } from "./Icons/skin_extraction.svg";
import { ReactComponent as SemanticSegmentationIcon } from "./Icons/semantic_segmentation.svg";
import { ReactComponent as ClassificationIcon } from "./Icons/classification.svg";

import { ReactComponent as OriginalImageIcon } from "./Icons/original_image.svg";
import { ReactComponent as IllnessMaskIcon } from "./Icons/illness_mask.svg";
import { ReactComponent as BodySegmentationMaskIcon } from "./Icons/body_segmentation_mask.svg";
import { ReactComponent as OverlayMaskIcon } from "./Icons/overlay_mask.svg";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

const SettingsScreen = () => {
  const [headColor, setHeadColor] = useState("#FFFFFF");
  const [torsoColor, setTorsoColor] = useState("#FFFFFF");
  const [leftArmColor, setLeftArmColor] = useState("#FFFFFF");
  const [rightArmColor, setRightArmColor] = useState("#FFFFFF");
  const [leftLegColor, setLeftLegColor] = useState("#FFFFFF");
  const [rightLegColor, setRightLegColor] = useState("#FFFFFF");
  const [bsBgColor, setBsBgColor] = useState("#000000");

  const [psoriasisColor, setPsoriasisColor] = useState("#00FF00");
  const [skinColor, setSkinColor] = useState("#0000FF");
  const [bodySegmentationSkinColor, setBodySegmentationSkinColor] =
    useState("#000000");
  const [illnessBgColor, setIllnessBgColor] = useState("#000000");

  const [skinZC, setSkinZC] = useState(0.5);
  const [segmentationZC, setSegmentationZC] = useState(0.5);
  const [classificationZC, setClassificationZC] = useState(0.5);

  const [originalImageW, setOriginalImageW] = useState(1);
  const [illnessMaskW, setIllnessMaskW] = useState(0.5);
  const [bodySegmentationMaskW, setBodySegmentationMaskW] = useState(0.5);
  const [overlayMaskW, setOverlayMaskW] = useState(0.5);

  // API Start
  const { response, axiosFetch } = useAxios();

  const handleSubmit = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "POST",
      url: "user/default_preferences",
      sendSignal: false,
      requestConfig: {
        head_color: headColor,
        torso_color: torsoColor,
        left_arm_color: leftArmColor,
        right_arm_color: rightArmColor,
        left_leg_color: leftLegColor,
        right_leg_color: rightLegColor,
        bs_bg_color: bsBgColor,

        psoriasis_color: psoriasisColor,
        skin_color: skinColor,
        body_segmentation_skin_color: bodySegmentationSkinColor,
        illness_bg_color: illnessBgColor,

        skin_zc: skinZC,
        segmentation_zc: segmentationZC,
        classification_zc: classificationZC,

        original_image_w: originalImageW,
        illness_mask_w: illnessMaskW,
        body_segmentation_mask_w: bodySegmentationMaskW,
        overlay_mask_w: overlayMaskW,
      },
    });
  };

  const getData = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "GET",
      url: "user/default_preferences",
      sendSignal: false,
      requestConfig: {},
    });
  };

  useEffect(() => {
    // Obtain data from API on initial page load.
    getData();
  }, []);

  // RESPONSE Detection
  useEffect(() => {
    // console.log(response);
    if (response?.action_code === "preferences_sent") {
      if (response?.payload) {
        const preferencesDataResponse = response.payload?.user_preferences;

        if (preferencesDataResponse) {
          setHeadColor(preferencesDataResponse?.head_color || "#FFFFFF");
          setTorsoColor(preferencesDataResponse?.torso_color || "#FFFFFF");
          setLeftArmColor(preferencesDataResponse?.left_arm_color || "#FFFFFF");
          setRightArmColor(
            preferencesDataResponse?.right_arm_color || "#FFFFFF"
          );
          setLeftLegColor(preferencesDataResponse?.left_leg_color || "#FFFFFF");
          setRightLegColor(
            preferencesDataResponse?.right_leg_color || "#FFFFFF"
          );
          setBsBgColor(preferencesDataResponse?.bs_bg_color || "#FFFFFF");

          setPsoriasisColor(
            preferencesDataResponse?.psoriasis_color || "#FFFFFF"
          );
          setSkinColor(preferencesDataResponse?.skin_color || "#FFFFFF");
          setBodySegmentationSkinColor(
            preferencesDataResponse?.body_segmentation_skin_color || "#FFFFFF"
          );
          setIllnessBgColor(
            preferencesDataResponse?.illness_bg_color || "#FFFFFF"
          );

          setSkinZC(preferencesDataResponse?.skin_zc || 0.5);
          setSegmentationZC(preferencesDataResponse?.segmentation_zc || 0.5);
          setClassificationZC(
            preferencesDataResponse?.classification_zc || 0.5
          );

          setOriginalImageW(
            preferencesDataResponse?.original_image_w !== null &&
              preferencesDataResponse?.original_image_w !== undefined
              ? preferencesDataResponse.original_image_w
              : 1
          );

          setIllnessMaskW(
            preferencesDataResponse?.illness_mask_w !== null &&
              preferencesDataResponse?.illness_mask_w !== undefined
              ? preferencesDataResponse.illness_mask_w
              : 1
          );

          setBodySegmentationMaskW(
            preferencesDataResponse?.body_segmentation_mask_w !== null &&
              preferencesDataResponse?.body_segmentation_mask_w !== undefined
              ? preferencesDataResponse.body_segmentation_mask_w
              : 1
          );

          setOverlayMaskW(
            preferencesDataResponse?.overlay_mask_w !== null &&
              preferencesDataResponse?.overlay_mask_w !== undefined
              ? preferencesDataResponse.overlay_mask_w
              : 1
          );
        }
      }
    }

    if (response?.action_code === "preferences_updated") {
      toastManager(
        "success",
        "Updated Successfully!",
        "Default preferences have been updated successfully."
      );
      // Refresh settings according to data saved on server.
      getData();
    }
  }, [response]);
  // API End

  return (
    <div className="flex flex-col w-full pb-4 md:pb-0 Xbg-red-500">
      <div className="flex flex-col md:flex-row items-center justify-between w-full Xbg-red-500">
        <div className="flex flex-row items-center justify-start w-full Xbg-green-500">
          <Heading1 headingText={"Default Preferences"} />
        </div>
        <div className="flex flex-row items-center justify-end w-full h-full pt-5 pb-2 space-x-4 Xbg-yellow-500">
          <SquareIconNavButton
            Icon={ArrowPathIcon}
            actionFunction={getData}
            activeColor={"active:bg-cyan-100"}
          />
        </div>
      </div>

      <div className="py-2" />

      <div
        className="flex flex-col items-center justify-center w-full 
                      border border-neutral-300 rounded-xl p-4"
      >
        <Heading2 headingText={"Body Segmentation Mask Colors"} />

        <div className="py-3" />

        <div className="flex flex-col-reverse lg:flex-row items-center justify-center w-full">
          <div className="flex flex-[1] flex-col items-center justify-center h-[27rem] w-full pt-6 lg:pt-0">
            <ColorPickerRow
              colorTitle={"Head"}
              colorValue={headColor}
              setColorValue={setHeadColor}
            />
            <div className="py-2" />
            <ColorPickerRow
              colorTitle={"Torso"}
              colorValue={torsoColor}
              setColorValue={setTorsoColor}
            />
            <div className="py-2" />
            <ColorPickerRow
              colorTitle={"Left Arm"}
              colorValue={leftArmColor}
              setColorValue={setLeftArmColor}
            />
            <div className="py-2" />
            <ColorPickerRow
              colorTitle={"Right Arm"}
              colorValue={rightArmColor}
              setColorValue={setRightArmColor}
            />
            <div className="py-2" />
            <ColorPickerRow
              colorTitle={"Left Leg"}
              colorValue={leftLegColor}
              setColorValue={setLeftLegColor}
            />
            <div className="py-2" />
            <ColorPickerRow
              colorTitle={"Right Leg"}
              colorValue={rightLegColor}
              setColorValue={setRightLegColor}
            />
            <div className="py-2" />
            <ColorPickerRow
              colorTitle={"Background"}
              colorValue={bsBgColor}
              setColorValue={setBsBgColor}
            />
          </div>

          <div className="lg:px-10" />

          {/* Body diagram */}
          <div className="flex flex-[1] flex-col items-center justify-center w-full">
            <div
              className="flex flex-col items-center justify-center w-full rounded-lg h-[27rem]"
              style={{ backgroundColor: bsBgColor }}
            >
              <div>
                <Head
                  className="max-w-[7rem] min-w-[7rem]"
                  style={{ fill: headColor }}
                  type="color"
                />
              </div>
              <div className="flex flex-row items-start justify-center">
                <LeftArm
                  className="max-w-[7rem] min-w-[7rem]"
                  style={{ fill: leftArmColor }}
                />
                <Torso
                  className="max-w-[7rem] min-w-[7rem]"
                  style={{ fill: torsoColor }}
                />
                <RightArm
                  className="max-w-[7rem] min-w-[7rem]"
                  style={{ fill: rightArmColor }}
                />
              </div>
              <div className="flex flex-row items-start justify-center">
                <LeftLeg
                  className="max-w-[7rem] min-w-[7rem]"
                  style={{ fill: leftLegColor }}
                />
                <RightLeg
                  className="max-w-[7rem] min-w-[7rem]"
                  style={{ fill: rightLegColor }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="py-2" />

      <div
        className="flex flex-col items-center justify-center w-full 
                      border border-neutral-300 rounded-xl p-4"
      >
        <Heading2 headingText={"Illness Mask Colors"} />

        <div className="py-3" />

        <div className="flex flex-[1] flex-col items-center justify-center w-full">
          <div className="flex flex-col lg:flex-row items-center justify-center w-full">
            <ColorPickerRow
              colorTitle={"Psoriasis"}
              colorValue={psoriasisColor}
              setColorValue={setPsoriasisColor}
            />

            <div className="py-2 lg:py-0 lg:px-10" />

            <ColorPickerRow
              colorTitle={"Skin"}
              colorValue={skinColor}
              setColorValue={setSkinColor}
            />
          </div>

          <div className="py-2 lg:px-10" />

          <div className="flex flex-col lg:flex-row items-center justify-center w-full">
            <ColorPickerRow
              colorTitle={"Segmentation Skin"}
              colorValue={bodySegmentationSkinColor}
              setColorValue={setBodySegmentationSkinColor}
            />

            <div className="py-2 lg:py-0 lg:px-10" />

            <ColorPickerRow
              colorTitle={"Background"}
              colorValue={illnessBgColor}
              setColorValue={setIllnessBgColor}
            />
          </div>
        </div>
      </div>

      <div className="py-2" />

      <div
        className="flex flex-col items-center justify-center w-full 
                      border border-neutral-300 rounded-xl p-4"
      >
        <Heading2 headingText={"Default Zoom Coefficients"} />

        <div className="py-3" />

        <SliderRow
          sliderTitle={"Skin Extraction"}
          SliderIcon={SkinExtractionIcon}
          sliderValue={skinZC}
          setSliderValue={setSkinZC}
          sliderMin={0.1}
          sliderMax={1}
          sliderStep={0.05}
        />
        <div className="py-2" />
        <SliderRow
          sliderTitle={"Semantic Segmentation"}
          SliderIcon={SemanticSegmentationIcon}
          sliderValue={segmentationZC}
          setSliderValue={setSegmentationZC}
          sliderMin={0.1}
          sliderMax={1}
          sliderStep={0.05}
        />
        <div className="py-2" />
        <SliderRow
          sliderTitle={"Classification"}
          SliderIcon={ClassificationIcon}
          sliderValue={classificationZC}
          setSliderValue={setClassificationZC}
          sliderMin={0.1}
          sliderMax={1}
          sliderStep={0.05}
        />
      </div>

      <div className="py-2" />

      <div
        className="flex flex-col items-center justify-center w-full 
                      border border-neutral-300 rounded-xl p-4"
      >
        <Heading2 headingText={"Default Blending Weights"} />

        <div className="py-3" />

        <SliderRow
          sliderTitle={"Original Image"}
          SliderIcon={OriginalImageIcon}
          sliderValue={originalImageW}
          setSliderValue={setOriginalImageW}
          sliderMin={0}
          sliderMax={1}
          sliderStep={0.05}
        />
        <div className="py-2" />
        <SliderRow
          sliderTitle={"Illness Mask"}
          SliderIcon={IllnessMaskIcon}
          sliderValue={illnessMaskW}
          setSliderValue={setIllnessMaskW}
          sliderMin={0}
          sliderMax={1}
          sliderStep={0.05}
        />
        <div className="py-2" />
        <SliderRow
          sliderTitle={"Body Segmentation Mask"}
          SliderIcon={BodySegmentationMaskIcon}
          sliderValue={bodySegmentationMaskW}
          setSliderValue={setBodySegmentationMaskW}
          sliderMin={0}
          sliderMax={1}
          sliderStep={0.05}
        />

        <div className="py-2" />
        <SliderRow
          sliderTitle={"Overlay Mask"}
          SliderIcon={OverlayMaskIcon}
          sliderValue={overlayMaskW}
          setSliderValue={setOverlayMaskW}
          sliderMin={0}
          sliderMax={1}
          sliderStep={0.05}
        />
      </div>

      <div className="py-3" />

      <div className="flex flex-row items-center justify-end w-full Xbg-red-500">
        <TextActionButton
          prompt={"Update Preferences"}
          actionFunction={handleSubmit}
        />
      </div>
    </div>
  );
};

export default SettingsScreen;
